import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Divider,
  CircularProgress,
} from "@mui/material";

import ProductStoreMatrixApi from "../api/ProductStoreMatrixApi";
import StoreGroupApi from "../api/StoreGroupApi";

import ProductsByStoreChart from "../components/Dashboard/Charts/ProductsByStoreChart";
import DivisionVsStoreChart from "../components/Dashboard/Charts/DivisionVsStoreChart";
import StatsByStoreAndDivisionTable from "../components/Dashboard/Charts/StatsByStoreAndDivisionTable";
import DepartmentVsProductChart from "../components/Dashboard/Charts/DepartmentVsProductChart";
import StoreVsDepartmentChart from "../components/Dashboard/Charts/StoreVsDepartmentChart";
// import GroupVsStoreChart from "../components/Dashboard/Charts/GroupVsStoreChart";

import ChartDialog from "../components/Dashboard/DialogBox/ChartDialog";

const prioritizeAndSortStores = (storeNames) => {
  return storeNames.sort((a, b) => {
    if (a === "ECOMMERCE") return -1; // "ECOMMERCE" comes first
    if (b === "ECOMMERCE") return 1;
    return a.localeCompare(b); // Sort alphabetically
  });
};

// Update the calculateStatsByDivision function to sort stores
const calculateStatsByDivision = (stores, groupings, mappings, products) => {
  // Create a mapping of store_id to groupings
  const groupingsMap = groupings.reduce((acc, grouping) => {
    acc[grouping.store_id] = grouping.groups;
    return acc;
  }, {});

  // Create a mapping of store_id to selected products
  const mappingLookup = mappings.reduce((acc, mapping) => {
    acc[mapping.store_id] = acc[mapping.store_id] || [];
    acc[mapping.store_id].push(mapping.style_color);
    return acc;
  }, {});

  // Calculate stats by division
  const unsortedStats = stores.map((store) => {
    const storeId = parseInt(store.id);
    const groups = groupingsMap[storeId] || { mens: "-", womens: "-" }; // Fallback if no grouping is found

    // Get all selected products for this store
    const selectedProducts = mappingLookup[storeId] || [];

    // Count Men's and Women's products
    const mensCount = products.filter(
      (product) =>
        product.DIVISION.toLowerCase() === "mens apparel" &&
        selectedProducts.includes(product.STYLE_COLOR)
    ).length;

    const womensCount = products.filter(
      (product) =>
        product.DIVISION.toLowerCase() === "womens apparel" &&
        selectedProducts.includes(product.STYLE_COLOR)
    ).length;

    return {
      store: store.STORE_NAME,
      mensGroup: groups.mens,
      mensCount: mensCount,
      womensGroup: groups.womens,
      womensCount: womensCount,
    };
  });

  // Sort stats with ECOMMERCE first, then alphabetically
  return prioritizeAndSortStores(unsortedStats.map((stat) => stat.store)).map(
    (storeName) => unsortedStats.find((stat) => stat.store === storeName)
  );
};

const Dashboard = () => {
  const [chartData, setChartData] = useState({
    salesOverview: { series: [], categories: [] },
    divisionStats: { series: [], categories: [] },
    statsByDivision: { data: [] },
    productDistribution: { series: [], labels: [] },
    storeVsDepartment: { series: [], categories: [] },
    groupVsStore: { series: [], categories: [] },
  });
  const [isLoading, setIsLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState(null);

  const handleDialogClose = () => setDialogOpen(false);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);

      // Fetch data
      const productData = await ProductStoreMatrixApi.fetchAll();
      const { products, stores, mappings } = productData.data;
      const groupingData = await StoreGroupApi.fetchStoreGroupings();

      // Process mappings
      const formattedMappings = mappings.reduce((acc, mapping) => {
        acc[`${mapping.style_color}-${mapping.store_id}`] = mapping.is_selected;
        return acc;
      }, {});

      // Group data
      const groupMap = {};
      groupingData.data.forEach((grouping) => {
        const { store_id, groups } = grouping;
        Object.entries(groups).forEach(([division, groupKey]) => {
          if (!groupMap[groupKey]) groupMap[groupKey] = [];
          const store = stores.find((store) => parseInt(store.id) === store_id);
          if (store) groupMap[groupKey].push(store);
        });
      });

      // Products by Store
      const salesOverviewData = stores.map((store) => {
        const totalProducts = products.reduce((total, product) => {
          const key = `${product.STYLE_COLOR}-${store.id}`;
          return total + (formattedMappings[key] ? 1 : 0);
        }, 0);
        return { storeName: store.STORE_NAME, totalProducts };
      });

      const sortedSalesOverview = prioritizeAndSortStores(
        salesOverviewData.map((d) => d.storeName)
      ).map((storeName) => {
        const storeData = salesOverviewData.find(
          (store) => store.storeName === storeName
        );
        return {
          storeName,
          totalProducts: storeData?.totalProducts || 0,
        };
      });

      // Division vs Store
      // Update Division vs Store Data Generation
      const divisionStatsData = prioritizeAndSortStores(
        stores.map((store) => store.STORE_NAME)
      ).map((storeName) => {
        const store = stores.find((s) => s.STORE_NAME === storeName);
        const menProducts = products.filter(
          (product) =>
            product.DIVISION.toLowerCase() === "mens apparel" &&
            formattedMappings[`${product.STYLE_COLOR}-${store.id}`]
        ).length;
        const womenProducts = products.filter(
          (product) =>
            product.DIVISION.toLowerCase() === "womens apparel" &&
            formattedMappings[`${product.STYLE_COLOR}-${store.id}`]
        ).length;

        return {
          storeName,
          men: menProducts,
          women: womenProducts,
        };
      });

      // Product Distribution
      const productDistributionData = products.reduce((acc, product) => {
        const division = product.DEPARTMENT;
        acc[division] = (acc[division] || 0) + 1;
        return acc;
      }, {});

      // Generate Department vs Product Data
      const departmentStatsData = prioritizeAndSortStores(
        stores.map((store) => store.STORE_NAME)
      ).map((storeName) => {
        const store = stores.find((s) => s.STORE_NAME === storeName);

        // Group products by department for this store
        const departmentCounts = products.reduce((acc, product) => {
          const key = `${product.STYLE_COLOR}-${store.id}`;
          if (formattedMappings[key]) {
            acc[product.DEPARTMENT] = (acc[product.DEPARTMENT] || 0) + 1;
          }
          return acc;
        }, {});

        return {
          storeName,
          ...departmentCounts, // Spread department counts into this object
        };
      });

      // Aggregate Department Data for Chart
      const aggregatedDepartmentData = departmentStatsData.reduce(
        (acc, store) => {
          Object.keys(store).forEach((department) => {
            if (department !== "storeName") {
              acc[department] = (acc[department] || 0) + store[department];
            }
          });
          return acc;
        },
        {} // Initial empty accumulator
      );

      // Final Chart Data
      const departmentChartData = {
        series: Object.values(aggregatedDepartmentData),
        labels: Object.keys(aggregatedDepartmentData),
      };

      // Stats by Store and Division
      const statsByDivision = calculateStatsByDivision(
        stores,
        groupingData.data,
        mappings,
        products
      );

      // Store vs Department
      const storeVsDepartmentData = prioritizeAndSortStores(
        stores.map((store) => store.STORE_NAME)
      ).map((storeName) => {
        const store = stores.find((s) => s.STORE_NAME === storeName);

        // Group products by department for this store
        const departmentCounts = products.reduce((acc, product) => {
          const key = `${product.STYLE_COLOR}-${store.id}`;
          if (formattedMappings[key]) {
            acc[product.DEPARTMENT] = (acc[product.DEPARTMENT] || 0) + 1;
          }
          return acc;
        }, {});

        return {
          storeName,
          ...departmentCounts, // Spread department counts into this object
        };
      });

      // Groups vs Store
      // const groupVsStoreData = Object.entries(groupMap).map(
      //   ([groupKey, groupStores]) => ({
      //     groupKey,
      //     totalProducts: groupStores.reduce((total, store) => {
      //       const storeProducts = products.reduce((storeTotal, product) => {
      //         const key = `${product.STYLE_COLOR}-${store.id}`;
      //         return storeTotal + (formattedMappings[key] ? 1 : 0);
      //       }, 0);
      //       return total + storeProducts;
      //     }, 0),
      //   })
      // );

      // const groupVsStoreData = Object.entries(groupMap)
      //   .map(([groupKey, groupStores]) => ({
      //     groupKey,
      //     totalProducts: groupStores.reduce((total, store) => {
      //       const storeProducts = products.reduce((storeTotal, product) => {
      //         const key = `${product.STYLE_COLOR}-${store.id}`;
      //         return storeTotal + (formattedMappings[key] ? 1 : 0);
      //       }, 0);
      //       return total + storeProducts;
      //     }, 0),
      //   }))
      //   .sort((a, b) => a.groupKey.localeCompare(b.groupKey)); // Sort by groupKey

      // Group stores for Men's and Women's divisions
      // Group stores for Men's and Women's divisions
      const processDivisionData = (division) => {
        // Create a map to group stores by their division group (A, B, C, etc.)
        const divisionData = stores.reduce((acc, store) => {
          const storeGrouping = groupingData.data.find(
            (grouping) => grouping.store_id === parseInt(store.id)
          );

          if (storeGrouping && storeGrouping.groups[division]) {
            const group = storeGrouping.groups[division];
            acc[group] = acc[group] || [];
            acc[group].push({
              storeName: store.STORE_NAME,
              storeId: parseInt(store.id), // Include store ID for filtering
              productCount: products.filter(
                (product) =>
                  product.DIVISION.toLowerCase() === `${division} apparel` &&
                  mappings.some(
                    (mapping) =>
                      mapping.store_id === parseInt(store.id) &&
                      mapping.style_color === product.STYLE_COLOR &&
                      mapping.is_selected
                  )
              ).length,
            });
          }
          return acc;
        }, {});

        // Prepare categories and raw series data
        const categories = Object.keys(divisionData).sort(); // Sorted groups

        // const rawSeries = categories.map((group) => ({
        //   name: group,
        //   data: divisionData[group].map((store) => ({
        //     productCount: store.productCount,
        //     storeName: store.storeName,
        //     storeId: store.storeId, // Include store ID for filtering
        //     group: group,
        //   })),
        // }));

        // console.log("Raw series: ", rawSeries)

        // // Align series data with categories
        // const prepareSeriesData = (stores, rawSeries) => {
        //   // Sort and filter distinct stores, excluding ECOMMERCE
        //   const sortedStores = [...new Set(stores)]
        //     .filter((store) => store.storeName !== "ECOMMERCE")

        //   console.log(sortedStores)
        //   return rawSeries.map((seriesItem) => {
        //     const alignedData = sortedStores.map((store) => {
        //       const matchingData = seriesItem.data.find(
        //         (data) => data.storeName === store.storeName // Match by store name
        //       );

        //       if (matchingData) {
        //         return {
        //           productCount: matchingData.productCount,
        //           storeName: matchingData.storeName,
        //           storeId: matchingData.storeId,
        //           group: matchingData.group,
        //         };
        //       }

        //       // Return default values for missing data
        //       return {
        //         productCount: null,
        //         storeName: store.storeName,
        //         storeId: store.storeId,
        //         group: store.group,
        //       };
        //     });

        //     return {
        //       name: seriesItem.name,
        //       data: alignedData,
        //     };
        //   });
        // };

        // const alignedSeries = prepareSeriesData(chartData.stores, rawSeries);
        // console.log("Aligned Series", alignedSeries)

        // const alignedSeries = prepareSeriesData(categories, rawSeries);
        // Flatten the data for each group
        const series = categories.map((category) => {
          const storesInGroup = divisionData[category] || [];
          return {
            name: category, // Group name
            data: storesInGroup.map((store) => ({
              productCount: store.productCount,
              storeName: store.storeName,
              storeId: store.storeId,
              group: category, // Include group name
            })),
          };
        });
        return {
          categories, // Groups (A, B, C, etc.)
          series, //: alignedSeries, // Aligned series data with productCount
        };
      };

      const mensChartData = processDivisionData("mens");
      const womensChartData = processDivisionData("womens");

      // console.log(mensChartData);
      // console.log(womensChartData);

      setChartData({
        salesOverview: {
          series: [
            {
              name: "Total Products",
              data: sortedSalesOverview.map((d) => d.totalProducts),
            },
          ],
          categories: sortedSalesOverview.map((d) => d.storeName),
        },
        divisionStats: {
          series: [
            {
              name: "Men's Division",
              data: divisionStatsData.map((d) => d.men),
            },
            {
              name: "Women's Division",
              data: divisionStatsData.map((d) => d.women),
            },
          ],
          categories: divisionStatsData.map((d) => d.storeName),
        },
        statsByDivision: {
          data: statsByDivision,
        },
        productDistribution: {
          series: departmentChartData.series,
          labels: departmentChartData.labels,
        },
        storeVsDepartment: {
          series: Object.keys(productDistributionData).map((department) => ({
            name: department,
            data: storeVsDepartmentData.map((store) => store[department] || 0),
          })),
          categories: storeVsDepartmentData.map((store) => store.storeName),
        },
        // groupVsStore: {
        //   series: [
        //     {
        //       name: "Products",
        //       data: groupVsStoreData.map((d) => d.totalProducts),
        //     },
        //   ],
        //   categories: groupVsStoreData.map((d) => d.groupKey),
        // },
        mensChart: mensChartData,
        womensChart: womensChartData,
        products,
        stores,
        mappings,
      });
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "75vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const handleBarClick = (storeIndex) => {
    // Get sorted store names
    const sortedStoreNames = prioritizeAndSortStores(
      chartData.stores.map((store) => store.STORE_NAME)
    );

    // Get the selected store name based on the sorted order
    const storeName = sortedStoreNames[storeIndex].toUpperCase();

    // Find the store object by matching the store name
    const selectedStore = chartData.stores.find(
      (store) => store.STORE_NAME === storeName
    );

    if (!selectedStore) {
      console.error("Store not found for the selected index:", storeIndex);
      return;
    }

    const storeId = parseInt(selectedStore.id);

    // Filter products for the selected store using mappings
    const products = chartData.products.filter((product) =>
      chartData.mappings.some(
        (mapping) =>
          mapping.store_id === storeId &&
          mapping.style_color === product.STYLE_COLOR &&
          mapping.is_selected
      )
    );

    const rows = products.map((product, index) => ({
      id: index + 1,
      styleCode: product.STYLE_CODE.toUpperCase(),
      description: product.STYLE_DESC_ENG.toUpperCase(),
      colorCode: product.COLOR_CODE.toUpperCase(),
      colorEng: product.COLOR_ENG.toUpperCase(),
    }));

    const columns = [
      { field: "styleCode", headerName: "STYLE CODE", flex: 1 },
      { field: "description", headerName: "DESCRIPTION", flex: 2 },
      { field: "colorCode", headerName: "COLOR CODE", flex: 1 },
      { field: "colorEng", headerName: "COLOR ENG", flex: 1 },
    ];

    setDialogContent({ title: storeName, rows, columns });
    setDialogOpen(true);
  };

  const handleDivisionClick = (storeIndex, divisionIndex) => {
    // Get sorted store names
    const sortedStoreNames = prioritizeAndSortStores(
      chartData.stores.map((store) => store.STORE_NAME)
    );

    // Get the selected store name based on the sorted order
    const storeName = sortedStoreNames[storeIndex].toUpperCase();

    // Find the store object by matching the store name
    const selectedStore = chartData.stores.find(
      (store) => store.STORE_NAME === storeName
    );

    if (!selectedStore) {
      console.error("Store not found for the selected index:", storeIndex);
      return;
    }

    const storeId = parseInt(selectedStore.id);

    // Determine the selected division based on the index
    const division = divisionIndex === 0 ? "mens apparel" : "womens apparel";

    // Filter products for the selected store and division
    const products = chartData.products.filter((product) =>
      chartData.mappings.some(
        (mapping) =>
          mapping.store_id === storeId &&
          mapping.style_color === product.STYLE_COLOR &&
          mapping.is_selected &&
          product.DIVISION.toLowerCase() === division
      )
    );

    const rows = products.map((product, index) => ({
      id: index + 1,
      styleCode: product.STYLE_CODE.toUpperCase(),
      description: product.STYLE_DESC_ENG.toUpperCase(),
      colorCode: product.COLOR_CODE.toUpperCase(),
      colorEng: product.COLOR_ENG.toUpperCase(),
    }));

    const columns = [
      { field: "styleCode", headerName: "STYLE CODE", flex: 1 },
      { field: "description", headerName: "DESCRIPTION", flex: 2 },
      { field: "colorCode", headerName: "COLOR CODE", flex: 1 },
      { field: "colorEng", headerName: "COLOR ENG", flex: 1 },
    ];

    setDialogContent({
      title: `${storeName} - ${division.toUpperCase()}`,
      rows,
      columns,
    });
    setDialogOpen(true);
  };

  const handleDepartmentClick = (departmentIndex) => {
    // Get the selected department name based on the slice index
    const departmentName =
      chartData.productDistribution.labels[departmentIndex].toUpperCase();

    // Filter products for the selected department across all stores
    const products = chartData.products.filter(
      (product) =>
        product.DEPARTMENT.toUpperCase() === departmentName &&
        chartData.mappings.some(
          (mapping) =>
            mapping.style_color === product.STYLE_COLOR && mapping.is_selected
        )
    );

    // Prepare rows and columns for the table
    const rows = products.map((product, index) => ({
      id: index + 1,
      styleCode: product.STYLE_CODE.toUpperCase(),
      description: product.STYLE_DESC_ENG.toUpperCase(),
      colorCode: product.COLOR_CODE.toUpperCase(),
      colorEng: product.COLOR_ENG.toUpperCase(),
    }));

    const columns = [
      { field: "styleCode", headerName: "STYLE CODE", flex: 1 },
      { field: "description", headerName: "DESCRIPTION", flex: 2 },
      { field: "colorCode", headerName: "COLOR CODE", flex: 1 },
      { field: "colorEng", headerName: "COLOR ENG", flex: 1 },
    ];

    // Open dialog with the department name as the title
    setDialogContent({
      title: departmentName, // Title only contains the department name
      rows,
      columns,
    });
    setDialogOpen(true);
  };

  const handleStoreDepartmentClick = (storeIndex, departmentIndex) => {
    // Get sorted store names
    const sortedStoreNames = prioritizeAndSortStores(
      chartData.stores.map((store) => store.STORE_NAME)
    );

    // Get the selected store name based on the sorted order
    const storeName = sortedStoreNames[storeIndex].toUpperCase();

    // Find the store object by matching the store name
    const selectedStore = chartData.stores.find(
      (store) => store.STORE_NAME === storeName
    );

    if (!selectedStore) {
      console.error("Store not found for the selected index:", storeIndex);
      return;
    }

    const storeId = parseInt(selectedStore.id);

    // Determine the department based on the series index
    const departmentName =
      chartData.storeVsDepartment.series[departmentIndex].name.toUpperCase();

    // Filter products for the selected store and department
    const products = chartData.products.filter(
      (product) =>
        product.DEPARTMENT.toUpperCase() === departmentName &&
        chartData.mappings.some(
          (mapping) =>
            mapping.store_id === storeId &&
            mapping.style_color === product.STYLE_COLOR &&
            mapping.is_selected
        )
    );

    const rows = products.map((product, index) => ({
      id: index + 1,
      styleCode: product.STYLE_CODE.toUpperCase(),
      description: product.STYLE_DESC_ENG.toUpperCase(),
      colorCode: product.COLOR_CODE.toUpperCase(),
      colorEng: product.COLOR_ENG.toUpperCase(),
    }));

    const columns = [
      { field: "styleCode", headerName: "STYLE CODE", flex: 1 },
      { field: "description", headerName: "DESCRIPTION", flex: 2 },
      { field: "colorCode", headerName: "COLOR CODE", flex: 1 },
      { field: "colorEng", headerName: "COLOR ENG", flex: 1 },
    ];

    setDialogContent({
      title: `${storeName} - ${departmentName}`,
      rows,
      columns,
    });
    setDialogOpen(true);
  };

  // const handleGroupStoreBarClick = (group, store, division) => {
  //   if (!group || !store) {
  //     console.error("Invalid group or store selection", { group, store });
  //     return;
  //   }

  //   const { storeName, storeId } = store;

  //   const products = chartData.products.filter((product) =>
  //     chartData.mappings.some((mapping) => {
  //       return (
  //         mapping.store_id === storeId &&
  //         mapping.style_color === product.STYLE_COLOR &&
  //         mapping.is_selected &&
  //         product.DIVISION?.toLowerCase() ===
  //           `${division.toLowerCase()} apparel`
  //       );
  //     })
  //   );

  //   const rows = products.map((product, index) => ({
  //     id: index + 1,
  //     styleCode: product.STYLE_CODE.toUpperCase(),
  //     description: product.STYLE_DESC_ENG.toUpperCase(),
  //     colorCode: product.COLOR_CODE.toUpperCase(),
  //     colorEng: product.COLOR_ENG.toUpperCase(),
  //   }));

  //   const columns = [
  //     { field: "styleCode", headerName: "STYLE CODE", flex: 1 },
  //     { field: "description", headerName: "DESCRIPTION", flex: 2 },
  //     { field: "colorCode", headerName: "COLOR CODE", flex: 1 },
  //     { field: "colorEng", headerName: "COLOR ENG", flex: 1 },
  //   ];

  //   setDialogContent({
  //     title: `${division.toUpperCase()} - Group ${group} - ${storeName}`,
  //     rows,
  //     columns,
  //   });
  //   setDialogOpen(true);
  // };

  return (
    <Box sx={{ padding: "20px" }}>
      <Typography variant="h6" sx={{ marginBottom: 1 }}>
        Dashboard - Store Matrix
      </Typography>
      <Divider sx={{ marginY: 2 }} />

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Products by Store */}
        <ProductsByStoreChart
          data={chartData.salesOverview}
          onBarClick={handleBarClick}
        />
        {/* Division vs Store */}
        <DivisionVsStoreChart
          data={chartData.divisionStats}
          onStackClick={(storeIndex, divisionIndex) =>
            handleDivisionClick(storeIndex, divisionIndex)
          }
        />
        {/* Stats by Store and Division */}
        <StatsByStoreAndDivisionTable
          statsByDivision={chartData.statsByDivision}
        />
        {/* Department vs Product */}
        <DepartmentVsProductChart
          data={chartData.productDistribution}
          onSliceClick={(departmentIndex) =>
            handleDepartmentClick(departmentIndex)
          }
        />
        {/* Store vs Department */}
        <StoreVsDepartmentChart
          data={chartData.storeVsDepartment}
          onBarClick={(storeIndex, departmentIndex) =>
            handleStoreDepartmentClick(storeIndex, departmentIndex)
          }
        />

        {/* Groups vs Store */}
        {/* Men's Division Chart */}
        {/* <GroupVsStoreChart
          data={chartData.mensChart}
          division="Men's"
          onBarClick={(group, store, division) => {
            handleGroupStoreBarClick(group, store, "mens"); // Pass data to the handler
          }}
        /> */}
        {/* Women's Division Chart */}
        {/* <GroupVsStoreChart
          data={chartData.womensChart}
          division="Women's"
          onBarClick={(group, store, division) => {
            handleGroupStoreBarClick(group, store, "womens"); // Pass data to the handler
          }}
        /> */}
      </Grid>

      <ChartDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        title={dialogContent?.title}
        rows={dialogContent?.rows}
        columns={dialogContent?.columns}
      />
    </Box>
  );
};

export default Dashboard;
